<script setup>
import ApikomTaskTable from '@frontJs/components/tables/projectManagement/tasks/ApikomTaskTable.vue';
import ApikomTaskTimeTable from '@frontJs/components/tables/projectManagement/tasks/ApikomTaskTimeTable.vue';
import { router, usePage } from '@inertiajs/vue3';
import DeleteAction from '@js/components/actions/DeleteAction.vue';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import ComboboxField from '@js/components/fields/ComboboxField.vue';
import EditorField from '@js/components/fields/EditorField.vue';
import InputField from '@js/components/fields/InputField.vue';
import ListboxField from '@js/components/fields/ListboxField.vue';
import SwitchField from '@js/components/fields/SwitchField.vue';
import TimeField from '@js/components/fields/TimeField.vue';
import FormColumn from '@js/components/forms/FormColumn.vue';
import FormContent from '@js/components/forms/FormContent.vue';
import FormFooter from '@js/components/forms/FormFooter.vue';
import FormGrid from '@js/components/forms/FormGrid.vue';
import FormRoot from '@js/components/forms/FormRoot.vue';
import TabLayout from '@js/components/tabs/TabLayout.vue';
import { goBack, useRoute } from '@js/utils/route';
import { toastError, toastSaved } from '@js/utils/toast';
import { useLocalStorage, useUrlSearchParams } from '@vueuse/core';
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { array, object, string } from 'yup';

const {
    task,
    taskTypes: types,
    taskRecurrentTypes: recurrentTypes,
    taskThemes: themes,
    taskStatuses: statuses,
    taskPriorities: priorities,
    tenants,
    users,
} = defineProps({
    task: {
        required: false,
        type: Object,
    },
    taskTypes: {
        required: true,
        type: Array,
    },
    taskRecurrentTypes: {
        required: true,
        type: Array,
    },
    taskThemes: {
        required: true,
        type: Array,
    },
    taskStatuses: {
        required: true,
        type: Array,
    },
    taskPriorities: {
        required: true,
        type: Array,
    },
    tenants: {
        required: true,
        type: Array,
    },
    users: {
        required: true,
        type: Array,
    },
});

const { route } = useRoute();

const isSubmitting = ref(false);

const formRef = ref(null);

const selectedTab = ref('information');

const currentUser = computed(() => usePage().props.currentUser);

const params = useUrlSearchParams('history');

const form = reactive({
    name: params.name ? params.name : task ? task.name : '',
    description: task ? task.description : '',
    internalComment: task ? task.internalComment : '',
    type: params.type
        ? types.find((type) => type.value === params.type)
        : task
          ? types.find((type) => type.value === task.type)
          : types[0],
    recurrentType:
        task && task.recurrentType
            ? recurrentTypes.find(
                  (recurrentType) => recurrentType.value === task.recurrentType,
              )
            : null,
    theme: params.theme
        ? themes.find((theme) => theme.value === params.theme)
        : task
          ? themes.find((theme) => theme.value === task.theme)
          : useLocalStorage(
                'default-apikom-task-theme',
                currentUser.value.groups && currentUser.value.groups.length > 0
                    ? themes.find(
                          (theme) =>
                              theme.value === currentUser.value.groups[0].key,
                      )
                    : themes[0],
            ),
    status: task
        ? statuses.find((status) => status.value === task.status)
        : statuses[0],
    priority: task
        ? priorities.find((priority) => priority.value === task.priority)
        : priorities[1],
    deadline: task ? task.deadline : new Date().toISOString().split('T')[0],
    tenants: [],
    tenant: task ? tenants.find((tenant) => tenant.id === task.tenantId) : null,
    principalUsers: params.noOneResponsible
        ? []
        : task
          ? users.filter((user) => task.principalUserIds.includes(user.id))
          : users.filter((user) => user.id === currentUser.value.id),
    noPrincipalUsers: params.noOneResponsible
        ? []
        : task
          ? users.filter((user) => task.noPrincipalUserIds.includes(user.id))
          : [],
    noOneResponsible: params.noOneResponsible
        ? true
        : task
          ? task.userIds.length === 0
          : false,
    taskTime: {
        date: new Date().toISOString().split('T')[0],
        hours: '',
        minutes: '',
        user:
            users.find((user) => user.id === currentUser.value.id) ?? users[0],
    },
});

const formattedPrincipalUsers = computed(() => {
    return users.filter((user) => {
        for (let i = 0; i < form.noPrincipalUsers.length; i++) {
            if (form.noPrincipalUsers[i].id === user.id) {
                return false;
            }
        }

        return true;
    });
});

const formattedNoPrincipalUsers = computed(() => {
    return users.filter((user) => {
        for (let i = 0; i < form.principalUsers.length; i++) {
            if (form.principalUsers[i].id === user.id) {
                return false;
            }
        }

        return true;
    });
});

const formattedSelectedUsers = computed(() => {
    return users.filter((user) => {
        for (let i = 0; i < form.principalUsers.length; i++) {
            if (form.principalUsers[i].id === user.id) {
                return true;
            }
        }

        for (let i = 0; i < form.noPrincipalUsers.length; i++) {
            if (form.noPrincipalUsers[i].id === user.id) {
                return true;
            }
        }

        return false;
    });
});

const formattedNamePrefix = computed(() => {
    let namePrefix = task
        ? `[${form.tenant.name}]`
        : `[${form.tenants.map((tenant) => tenant.name).join(', ')}]`;
    namePrefix += ` [${form.type.abbreviation}]`;
    namePrefix += ` [${form.theme.label}]`;

    if (form.recurrentType) {
        namePrefix += ` [${'REC'}]`;
        namePrefix += ` [${form.recurrentType.abbreviation}]`;
    }

    namePrefix += ` -`;

    return namePrefix;
});

const formattedStatuses = computed(() => {
    return statuses.filter((status) => {
        if (form.recurrentType) {
            return status.selectableForRecurrentTask;
        } else {
            return status.selectableForSingleTask;
        }
    });
});

const tabs = computed(() => {
    const tabs = {
        information: {
            title: 'Informations',
            contentSlotName: 'information',
            forceMount: true,
        },
    };

    if (!form.noOneResponsible) {
        tabs.time = {
            title: 'Temps',
            contentSlotName: 'time',
            forceMount: !task,
        };
    }

    if (task && !task.isChild) {
        tabs.subTasks = {
            title: 'Sous-tâches',
            contentSlotName: 'subTasks',
        };
    }

    return tabs;
});

const validationSchema = object({
    name: string().required('Ce champ est obligatoire.'),
    deadline: string().required('Ce champ est obligatoire.'),
    type: object().required('Ce champ est obligatoire.'),
    recurrentType: object().nullable(),
    theme: object().required('Ce champ est obligatoire.'),
    status: object().required('Ce champ est obligatoire.'),
    priority: object().required('Ce champ est obligatoire.'),
    tenants: array().min(1, 'Ce champ est obligatoire.'),
    principalUsers: array().when('noOneResponsible', {
        is: false,
        then: () => array().min(1, 'Ce champ est obligatoire.'),
    }),
    noPrincipalUsers: array().when('noOneResponsible', {
        is: false,
        then: () => array().min(1, 'Ce champ est obligatoire.'),
    }),
});

function submit() {
    isSubmitting.value = true;

    axios
        .post(
            route('front.projectManagement.task.postTask', {
                ...(task ? { task: task.id } : {}),
            }),
            {
                name: form.name,
                description: form.description,
                internalComment: form.internalComment,
                type: form.type.value,
                recurrentType: form.recurrentType?.value,
                theme: form.theme.value,
                status: form.status.value,
                priority: form.priority.value,
                deadline: form.deadline,
                principalUserIds: form.principalUsers.map((user) => user.id),
                noPrincipalUserIds: form.noPrincipalUsers.map(
                    (user) => user.id,
                ),
                taskTime: {
                    date: form.taskTime.date,
                    hours: form.taskTime.hours ?? '00',
                    minutes: form.taskTime.minutes,
                    userId: form.taskTime.user.id,
                },
                ...(task
                    ? { tenantId: form.tenant.id }
                    : {
                          tenantIds: form.tenants.map((tenant) => tenant.id),
                      }),
            },
        )
        .then(() => {
            toastSaved();

            goBack();
        })
        .catch((error) => {
            toastError(error);
        })
        .finally(() => {
            isSubmitting.value = false;
        });
}

watch(form, () => {
    if (form.recurrentType && form.status.value === 'open') {
        form.status = statuses.find((status) => status.value === 'to_process');
    }

    if (form.recurrentType === null && form.status.value === 'to_process') {
        form.status = statuses.find((status) => status.value === 'open');
    }

    if (form.recurrentType === null && form.status.value === 'processed') {
        form.status = statuses.find((status) => status.value === 'closed');
    }
});

watch(
    () => form.noOneResponsible,
    () => {
        if (form.noOneResponsible) {
            form.principalUsers = [];
            form.noPrincipalUsers = [];
        }
    },
);

watch(
    () => task,
    () => {
        console.log('task changed', task);
    },
);

onMounted(() => {
    router.on('before', (event) => {
        const url = new URL(window.location.href);
        const newUrl = event.detail.visit.url;

        if (
            !isSubmitting.value &&
            formRef.value &&
            formRef.value.getMeta()?.touched &&
            url.pathname !== newUrl.pathname
        ) {
            return confirm(
                'Voulez-vous vraiment quitter sans sauvegarder les modifications ?',
            );
        }
    });
});
</script>

<template>
    <FormRoot
        ref="formRef"
        :validation-schema="validationSchema"
        :submit="submit"
        v-slot="{ errors }"
    >
        <TabLayout v-model:selected-tab="selectedTab" :tabs="tabs">
            <template #information>
                <FormColumn>
                    <FormContent>
                        <FormGrid :columns="3">
                            <ListboxField
                                v-model="form.type"
                                field-id="type"
                                field-name="type"
                                label="Type"
                                :options="types"
                                required
                                :disabled="task && task.isChild"
                            >
                                <template #buttonContent>
                                    {{ form.type.label }}
                                </template>

                                <template #optionContent="{ option }">
                                    {{ option.label }}
                                </template>
                            </ListboxField>

                            <ListboxField
                                v-model="form.recurrentType"
                                field-id="recurrentType"
                                field-name="recurrentType"
                                label="Récurrent"
                                :options="recurrentTypes"
                                has-empty-option
                                :disabled="task && task.isChild"
                            >
                                <template #buttonContent>
                                    {{ form.recurrentType.label }}
                                </template>

                                <template #optionContent="{ option }">
                                    {{ option.label }}
                                </template>
                            </ListboxField>

                            <ListboxField
                                v-model="form.theme"
                                field-id="theme"
                                field-name="theme"
                                label="Thème"
                                :options="themes"
                                required
                                :disabled="task && task.isChild"
                            >
                                <template #buttonContent>
                                    {{ form.theme.label }}
                                </template>

                                <template #optionContent="{ option }">
                                    {{ option.label }}
                                </template>
                            </ListboxField>
                        </FormGrid>
                    </FormContent>

                    <FormContent>
                        <FormGrid :columns="1">
                            <ComboboxField
                                v-if="task"
                                v-model="form.tenant"
                                :options="tenants"
                                label="Client"
                                field-name="tenant"
                                field-id="tenant"
                                filteredOptionKey="name"
                                required
                                :disabled="task && task.isChild"
                            >
                                <template #optionContent="{ option }">
                                    {{ option.name }}
                                </template>
                            </ComboboxField>

                            <ComboboxField
                                v-else
                                v-model="form.tenants"
                                :options="tenants"
                                label="Clients"
                                field-name="tenants"
                                field-id="tenants"
                                filteredOptionKey="name"
                                multiple
                                required
                            >
                                <template #optionContent="{ option }">
                                    {{ option.name }}
                                </template>
                            </ComboboxField>
                        </FormGrid>
                    </FormContent>

                    <FormContent>
                        <FormGrid :columns="1">
                            <InputField
                                v-model="form.name"
                                label="Nom"
                                placeholder="Nom"
                                field-name="name"
                                field-id="name"
                                type="text"
                                :max="138 - formattedNamePrefix.length"
                                required
                            >
                                <template #inputPrefix>
                                    {{ formattedNamePrefix }}
                                </template>
                            </InputField>
                        </FormGrid>
                    </FormContent>

                    <FormContent>
                        <FormGrid :columns="1">
                            <SwitchField
                                v-model="form.noOneResponsible"
                                label="Aucun responsable"
                                field-name="noOneResponsible"
                                field-id="noOneResponsible"
                            />
                        </FormGrid>

                        <FormGrid :columns="2">
                            <ComboboxField
                                v-model="form.principalUsers"
                                :options="formattedPrincipalUsers"
                                label="Responsables tâches (principaux)"
                                field-name="principalUsers"
                                field-id="principalUsers"
                                filteredOptionKey="fullName"
                                multiple
                                required
                                :disabled="
                                    (task && task.isChild) ||
                                    form.noOneResponsible
                                "
                            >
                                <template #optionContent="{ option }">
                                    {{ option.fullName }}
                                </template>
                            </ComboboxField>

                            <ComboboxField
                                v-model="form.noPrincipalUsers"
                                :options="formattedNoPrincipalUsers"
                                label="Responsables tâches (autres)"
                                field-name="noPrincipalTaskManagers"
                                field-id="noPrincipalTaskManagers"
                                filteredOptionKey="fullName"
                                multiple
                                :disabled="
                                    (task && task.isChild) ||
                                    form.noOneResponsible
                                "
                            >
                                <template #optionContent="{ option }">
                                    {{ option.fullName }}
                                </template>
                            </ComboboxField>
                        </FormGrid>
                    </FormContent>

                    <FormContent>
                        <FormGrid :columns="3">
                            <InputField
                                v-model="form.deadline"
                                label="Date limite"
                                placeholder="Date limite"
                                field-name="deadline"
                                field-id="deadline"
                                type="date"
                                required
                                :disabled="task && task.isChild"
                            />

                            <ListboxField
                                v-model="form.priority"
                                field-id="priority"
                                field-name="priority"
                                label="Priorité"
                                :options="priorities"
                                required
                                :disabled="task && task.isChild"
                            >
                                <template #buttonContent>
                                    {{ form.priority.label }}
                                </template>

                                <template #optionContent="{ option }">
                                    {{ option.label }}
                                </template>
                            </ListboxField>

                            <ListboxField
                                v-model="form.status"
                                field-id="status"
                                field-name="status"
                                label="Statut"
                                :options="formattedStatuses"
                                :disabled="task && task.isChild"
                                required
                            >
                                <template #buttonContent>
                                    {{ form.status.label }}
                                </template>

                                <template #optionContent="{ option }">
                                    {{ option.label }}
                                </template>
                            </ListboxField>
                        </FormGrid>
                    </FormContent>

                    <FormContent>
                        <FormGrid :columns="1">
                            <EditorField
                                v-model="form.description"
                                label="Description"
                                field-name="description"
                                field-id="description"
                                size="small"
                            />

                            <EditorField
                                v-model="form.internalComment"
                                label="Commentaire interne"
                                field-name="internalComment"
                                field-id="internalComment"
                                size="small"
                            />
                        </FormGrid>
                    </FormContent>
                </FormColumn>
            </template>

            <template #time>
                <FormColumn v-if="typeof task === 'undefined' || task === null">
                    <FormContent>
                        <div
                            class="grid grid-cols-1 gap-x-8 md:grid-cols-2 lg:grid-cols-4"
                        >
                            <InputField
                                v-model="form.taskTime.date"
                                label="Date"
                                placeholder="Date"
                                field-name="date"
                                field-id="date"
                                type="date"
                            />
                        </div>
                    </FormContent>

                    <FormContent>
                        <div
                            class="grid grid-cols-1 gap-x-8 md:grid-cols-2 lg:grid-cols-4"
                        >
                            <ListboxField
                                v-model="form.taskTime.user"
                                field-id="user"
                                field-name="user"
                                label="Responsable tâche"
                                :options="formattedSelectedUsers"
                            >
                                <template #buttonContent>
                                    {{ form.taskTime.user.fullName }}
                                </template>

                                <template #optionContent="{ option }">
                                    {{ option.fullName }}
                                </template>
                            </ListboxField>
                        </div>
                    </FormContent>

                    <FormContent>
                        <TimeField
                            v-model:hours="form.taskTime.hours"
                            v-model:minutes="form.taskTime.minutes"
                            hours-field-id="hours"
                            hours-field-name="hours"
                            :minutes="form.minutes"
                            minutes-field-id="minutes"
                            minutes-field-name="minutes"
                            label="Temps"
                        />
                    </FormContent>
                </FormColumn>

                <ApikomTaskTimeTable v-else :task="task" without-filters />
            </template>

            <template #subTasks>
                <ApikomTaskTable :task="task" />
            </template>
        </TabLayout>

        <FormFooter
            v-if="
                !['time', 'subTasks'].includes(selectedTab) ||
                typeof task === 'undefined' ||
                task === null
            "
            :is-submitting="isSubmitting"
            :errors="errors"
            without-save-button
        >
            <template #actions>
                <DeleteAction
                    v-if="task"
                    :url="
                        route('front.projectManagement.task.deleteTask', {
                            task: task.id,
                        })
                    "
                    :callback="
                        () => {
                            router.visit(
                                route('front.projectManagement.task.tasks'),
                                {
                                    preserveState: true,
                                    replace: true,
                                },
                            );
                        }
                    "
                >
                    <template #trigger>
                        <ActionButton type="secondary">
                            Supprimer
                        </ActionButton>
                    </template>
                </DeleteAction>

                <ActionButton
                    v-if="
                        !task &&
                        form.taskTime.hours === '' &&
                        form.taskTime.minutes === '' &&
                        selectedTab === 'information' &&
                        !form.noOneResponsible
                    "
                    :callback="() => (selectedTab = 'time')"
                >
                    Enregistrer
                </ActionButton>

                <ActionButton
                    v-else
                    submit
                    :disabled="Object.keys(errors).length > 0 || isSubmitting"
                    :loading="isSubmitting"
                >
                    <template v-if="!isSubmitting"> Enregistrer </template>

                    <template v-else> Enregistrement en cours... </template>
                </ActionButton>
            </template>
        </FormFooter>
    </FormRoot>
</template>
